import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import AppLayout from "@/layouts/AppLayout";
import AuthLayout from "@/layouts/AuthLayout";
import Home from "./Home";
import Profile from "./Profile";
import Assessments from "./Assessments";
import AssessmentPipeline from "@/pages/AssessmentPipeline";
import AssessmentResultPage from "@/pages/assessment/AssessmentResultPage";
import DebugAssessmentPage from "@/pages/assessment/DebugAssessmentPage";
import AssessmentsWaitlistPage from "@/pages/AssessmentsWaitlistPage";
import Notifications from "./Notifications";
import Toolbox from "./Toolbox";
import Invitations from "./Invitations";
import Tanks from "./Tanks";
import Points from "./Points";
import Security from "./Security";
import Feedback from "./Feedback";
// import Lottery from "./Lottery";
import Login from "./Login";
import Register from "./Register";
import VerifyEmail from "./VerifyEmail";
import ForgotPassword from "../auth/ForgotPassword";
import ResetPassword from "../auth/ResetPassword";
import PhoneVerification from "../auth/PhoneVerification";
import ResendConfirmation from "../auth/ResendConfirmation";
import ResendUnlock from "../auth/ResendUnlock";
/*
import QaListPage from "../encyclopedia/QaListPage";
import QaDetailPage from "../encyclopedia/QaDetailPage";
import ArticleListPage from "../encyclopedia/ArticleListPage";
import ArticleDetailPage from "../encyclopedia/ArticleDetailPage";
*/
import AboutPage from "../AboutPage";
// 动态法律页面组件（根据区域获取协议内容）
import TermsPage from "../public/TermsPage";
import PrivacyPage from "../public/PrivacyPage";
import JoinPage from "../JoinPage";
import ContactPage from "../ContactPage";
// import TagsIndexPage from "../tags/TagsIndexPage";
// import TagDetailPage from "../tags/TagDetailPage";
import { useUserStore } from "@/stores/userStore";
import RequireAuth from "@/components/auth/RequireAuth";
import { ConsentInterceptor } from "@/components/consent";

export default function AppRouter() {
  // 从页面中读取初始用户数据
  const initialUserData = React.useMemo(() => {
    try {
      const dataElement = document.getElementById("initial-user-data");
      if (dataElement?.textContent) {
        return JSON.parse(dataElement.textContent);
      }
    } catch (error) {
      console.error("解析用户数据失败:", error);
    }
    return null;
  }, []);

  // 同步初始用户数据到 Store
  const setUser = useUserStore((state) => state.setUser);
  const [isInitialized, setIsInitialized] = React.useState(false);

  React.useEffect(() => {
    // 设置加载状态，防止 RequireAuth 在初始化期间触发重定向
    useUserStore.setState({ isLoading: true });

    if (initialUserData) {
      setUser(initialUserData);
    } else {
      // 未登录时清除 store 中的用户数据（清除 localStorage 中的旧数据）
      useUserStore.setState({
        isAuthenticated: false,
        currentUser: null,
      });
    }

    // 完成初始化
    useUserStore.setState({ isLoading: false });
    setIsInitialized(true);
  }, [initialUserData, setUser]);

  // 在初始化完成之前显示加载状态
  if (!isInitialized) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* 认证相关页面 - 使用 AuthLayout（简化头部，无导航栏） */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/verify-phone" element={<PhoneVerification />} />
          <Route path="/resend-confirmation" element={<ResendConfirmation />} />
          <Route path="/resend-unlock" element={<ResendUnlock />} />
        </Route>

        {/* 使用 AppLayout 包裹其他路由 */}
        <Route element={<AppLayout user={initialUserData} />}>
          {/* 首页 - 公开 */}
          <Route path="/" element={<Home />} />

          {/* 评估记录 - 产品介绍页 */}
          <Route path="/assessments" element={<Assessments />} />

          {/* Waitlist 页面 - 公开 */}
          <Route
            path="/waitlist/assessments"
            element={<AssessmentsWaitlistPage />}
          />

          {/* 诊断流水线 - 需要登录 */}
          <Route
            path="/assessments/new"
            element={
              <RequireAuth>
                <AssessmentPipeline />
              </RequireAuth>
            }
          />

          {/* 查看单个评估记录 - 需要登录 */}
          <Route
            path="/assessments/:id"
            element={
              <RequireAuth>
                <AssessmentPipeline />
              </RequireAuth>
            }
          />

          {/* 评估结果页面 - 需要登录 */}
          <Route
            path="/assessments/:id/result"
            element={
              <RequireAuth>
                <AssessmentResultPage />
              </RequireAuth>
            }
          />

          {/* 调试页面 - 临时用于排查问题 */}
          <Route
            path="/assessments/:id/debug"
            element={
              <RequireAuth>
                <DebugAssessmentPage />
              </RequireAuth>
            }
          />

          {/* <Route path="/consultations" element={<Consultations />} /> */}

          {/* 乐透抽奖 - 隐藏 */}
          {/* <Route path="/lottery" element={<Lottery />} /> */}

          {/* 水花介绍页 - 重定向到积分页面 */}
          <Route path="/spray" element={<Navigate to="/points" replace />} />

          {/* 公开页面 - 关于我们、加入我们、联系我们、服务协议、隐私政策 */}
          <Route path="/about" element={<AboutPage />} />
          <Route path="/join" element={<JoinPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />

          {/* 标签页面 - 隐藏 */}
          {/* <Route path="/tags" element={<TagsIndexPage />} /> */}
          {/* <Route path="/tags/:slug" element={<TagDetailPage />} /> */}

          {/* 百科问答 - 隐藏 */}
          {/*
          <Route
            path="/encyclopedia/:speciesSlug/qas"
            element={<QaListPage />}
          />
          <Route
            path="/encyclopedia/:speciesSlug/qas/category/:categorySlug"
            element={<QaListPage />}
          />
          <Route
            path="/encyclopedia/:speciesSlug/qas/:slug"
            element={<QaDetailPage />}
          />
          */}

          {/* 百科文章 - 隐藏 */}
          {/*
          <Route
            path="/encyclopedia/:speciesSlug/articles"
            element={<ArticleListPage />}
          />
          <Route
            path="/encyclopedia/:speciesSlug/articles/category/:categorySlug"
            element={<ArticleListPage />}
          />
          <Route
            path="/encyclopedia/:speciesSlug/articles/:slug"
            element={<ArticleDetailPage />}
          />
          */}

          {/* 需要鉴权的路由 - 包含协议签署检查 */}
          <Route element={<RequireAuth />}>
            <Route element={<ConsentInterceptor />}>
              {/* 用户资料 */}
              <Route path="/profile" element={<Profile />} />

              {/* 通知中心 */}
              <Route path="/notifications" element={<Notifications />} />

              {/* 我的鱼缸 */}
              <Route path="/tanks" element={<Tanks />} />

              {/* 我的水花 */}
              <Route path="/points" element={<Points />} />

              {/* 账号安全 */}
              <Route path="/security" element={<Security />} />

              {/* 我的工具箱 */}
              <Route path="/toolbox" element={<Toolbox />} />

              {/* 邀请有礼 */}
              <Route path="/invitations" element={<Invitations />} />

              {/* 意见反馈 */}
              <Route path="/feedback" element={<Feedback />} />
            </Route>
          </Route>

          {/* 404 页面 */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
